import * as Sentry from '@sentry/browser'

import { shouldReportError } from './filters'

/**
 * Initialize Sentry with enhanced configuration
 * This should be called as early as possible in the app lifecycle
 */
export function initSentry(): void {
  const windowEnvironment = window._env_

  if (!windowEnvironment?.APP_SENTRY_DSN_URL) {
    console.info('Sentry DSN not configured, skipping initialization')
    return
  }

  Sentry.init({
    dsn: windowEnvironment?.APP_SENTRY_DSN_URL,

    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),

    enableLogs: true,

    integrations: [
      Sentry.captureConsoleIntegration({
        levels: ['error', 'warn'],
      }),

      Sentry.dedupeIntegration(),

      Sentry.reportingObserverIntegration(),

      Sentry.replayIntegration({
        // Mask sensitive text in replays for privacy
        maskAllText: true,
        maskAllInputs: true,
        // Block all media to reduce payload size
        blockAllMedia: true,
        // Sampling for network requests in replays
        networkDetailAllowUrls: [/^https:\/\/[^/]*\.ditto\.live/],
      }),

      Sentry.browserProfilingIntegration(),

      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
    ],

    // Trace propagation targets, only target our own backend
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/[^/]*\.ditto\.live/,
      /^https:\/\/api\./,
    ],

    // Session replay sampling. All errors result in a replay,
    // otherwise the session rate is 10%
    replaysSessionSampleRate:
      windowEnvironment.APP_ENV === 'production' ? 0.1 : 0,
    replaysOnErrorSampleRate:
      windowEnvironment.APP_ENV === 'production' ? 1.0 : 0,

    // Performance monitoring sampling
    tracesSampleRate: windowEnvironment.APP_ENV === 'production' ? 1.0 : 1.0,

    // Profiling sampling (based on traces sample rate)
    profilesSampleRate: windowEnvironment.APP_ENV === 'production' ? 1.0 : 0,

    environment: windowEnvironment.APP_ENV || 'development',

    // Enhanced error filtering and processing
    beforeSend(event, hint) {
      // Filter out known non-issues
      if (!shouldReportError(event, hint)) {
        return null
      }

      // Add portal version as breadcrumb
      const versionBreadcrumb: Sentry.Breadcrumb = {
        category: 'versions',
        level: 'debug',
        data: {
          PORTAL_FRONTEND_BUILD_VERSION: windowEnvironment.BUILD_VERSION,
        },
      }

      if (event.breadcrumbs) {
        event.breadcrumbs.push(versionBreadcrumb)
      } else {
        event.breadcrumbs = [versionBreadcrumb]
      }

      return event
    },

    // Send default PII
    sendDefaultPii: true,

    // Set max breadcrumbs to capture
    maxBreadcrumbs: 50,

    // Normalize depth for context objects
    normalizeDepth: 5,
  })
}

// Re-export Sentry for convenience
export * as Sentry from '@sentry/browser'

// Export context utilities
export {
  clearCustomContexts,
  setDatabaseContext,
  setOrganizationContext,
} from './context'
export {
  useManualSentryDatabaseContext,
  useSentryDatabaseContext,
} from './useSentryContext'

// Export async tracking utilities
export {
  captureWithContext,
  trackAsync,
  usePortalCallback,
  withErrorTracking,
} from './tracking'
